import { useState, useEffect, memo } from 'react';
import {
  EvacZone,
  RegionEvacZoneStyle,
  EvacZoneStatus,
  FormEvacZoneStatus,
} from 'shared/types';
import { Box } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  useGridApiRef,
  GridToolbarProps,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import {
  getEnabledEvacZoneStatuses,
  zoneRequiresApproval,
} from 'features/evacZones/utils';
import { useFormContext } from 'react-hook-form';
import { EvacZoneStyles } from '../../../../constants';
import {
  EvacuationZonesToolbar,
  EvacuationZonesToolbarProps,
} from './EvacuationZonesToolbar';
import { EvacZoneStatusChange } from '../../types';
import { zoneHasUpdates, applyAllFilters, copyZones } from './utils';
import { EvacuationZonesFooter } from './EvacuationZonesFooter';
import {
  EvacuationZonesTableRowData,
  getGridColumnConfiguration,
} from './gridColumns';
import { useEvacZoneSelection } from '../../hooks/useEvacZoneSelection';
import { useEvacZoneFilters } from '../../hooks/useEvacZoneFilters';
import { useEvacZoneManagingGeoEvents } from '../../hooks/useEvacZoneManagingGeoEvents';

// Wrapper component that combines DataGrid's toolbar props with our custom props
// This allows us to use our custom props without polluting global types
const EvacuationZonesToolbarWrapper = (
  props: GridToolbarProps,
): JSX.Element => {
  const customProps = props as unknown as EvacuationZonesToolbarProps;
  return <EvacuationZonesToolbar {...customProps} />;
};

type EvacuationZoneTableProps = {
  evacZones: EvacZone[];
  formZones: FormEvacZoneStatus[];
  zoneStyles: RegionEvacZoneStyle[];
  handleStatusChange: (changes: EvacZoneStatusChange[]) => number;
  disabled: boolean;
};

type FormValues = {
  releasePendingZoneUids?: string[];
};

const EvacuationZonesTable = ({
  evacZones,
  formZones,
  zoneStyles,
  handleStatusChange,
  disabled,
}: EvacuationZoneTableProps): JSX.Element => {
  const { t } = useTranslation();
  const searchQueryState = useState<string>('');
  const searchQuery = searchQueryState[0];
  const apiRef = useGridApiRef();
  const { watch } = useFormContext<FormValues>();

  const releasePendingZoneUids = watch('releasePendingZoneUids', [])!;

  // get all statuses enabled for the zone styles
  const enabledEvacZoneStatuses = getEnabledEvacZoneStatuses(zoneStyles);

  const { filters, isFiltered, handleCheckboxChange } = useEvacZoneFilters();

  const managingGeoEvents = useEvacZoneManagingGeoEvents(evacZones);

  const {
    rowSelectionModel,
    setRowSelectionModel,
    handleSetSelectedZoneStatus,
  } = useEvacZoneSelection({ disabled, handleStatusChange });

  // derive zones with their statuses
  const zonesWithStatus = evacZones.map((zone) => ({
    zone,
    status: formZones.find((s) => s.evacZone.uidV2 === zone.uidV2),
  }));

  // transform zones with status into rows for DataGrid
  const rows: EvacuationZonesTableRowData[] = zonesWithStatus.map(
    ({ zone, status }) => ({
      id: zone.uidV2,
      zone: zone.displayName,
      updates: zone,
      externalStatus: zone.externalStatus?.mapped,
      internalStatus: zone.status,
      status: status?.status,
      evacZoneStyle: zone.region.evacZoneStyle || EvacZoneStyles.default,
      hasUpdates: zoneHasUpdates(zone),
      requiresApproval: zoneRequiresApproval(zone, releasePendingZoneUids),
    }),
  );

  // callback for status change in action column
  const changeStatusHandler = (
    id: string,
    newStatus?: EvacZoneStatus,
  ): number => {
    return handleStatusChange([
      {
        uid: id,
        status: newStatus,
      },
    ]);
  };

  // apply filters (search and custom) to data grid
  useEffect(() => {
    applyAllFilters(apiRef, filters, searchQuery);
  }, [searchQuery, apiRef, filters]);

  const columns: GridColDef[] = getGridColumnConfiguration(
    t,
    changeStatusHandler,
    disabled,
    managingGeoEvents,
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box sx={{ minWidth: 720, height: 610 }}>
        <DataGrid
          apiRef={apiRef}
          initialState={{
            columns: {
              columnVisibilityModel: {
                hasUpdates: false, // Hide hasUpdates column, used for filtering only
              },
            },
            sorting: {
              sortModel: [{ field: 'updates', sort: 'asc' }],
            },
          }}
          sortingOrder={['asc', 'desc']} // Removes the "unsorted" step
          density="standard"
          rows={rows}
          columns={columns}
          getRowHeight={() => 'auto'}
          paginationModel={{ page: 0, pageSize: rows.length }}
          disableAutosize
          checkboxSelection
          disableColumnFilter
          disableColumnMenu
          hideFooterPagination
          rowSelectionModel={rowSelectionModel}
          isRowSelectable={({ row }) =>
            !disabled && !row.updates.isLocked && !row.requiresApproval
          }
          onRowSelectionModelChange={(model) => {
            if (!disabled) {
              setRowSelectionModel(model);
            }
          }}
          showToolbar
          slots={{
            toolbar: EvacuationZonesToolbarWrapper,
            footer: EvacuationZonesFooter,
          }}
          slotProps={{
            toolbar: {
              evacZoneStyles: zoneStyles,
              selected: rowSelectionModel.ids.size,
              searchQueryState,
              handleCopyZones: (zoneFilters) => {
                copyZones(zoneFilters, zonesWithStatus, t);
              },
              handleSetSelectedZoneStatus: (
                status: EvacZoneStatus | undefined,
                acceptExternalStatus: boolean,
              ) => {
                if (!disabled) {
                  return handleSetSelectedZoneStatus(
                    status,
                    acceptExternalStatus,
                  );
                }
                return 0; // Return a default value when disabled is true
              },
              enabledEvacZoneStatuses,
              filters,
              onCheckboxChange: handleCheckboxChange,
              isFiltered,
            } as EvacuationZonesToolbarProps,
          }}
          columnHeaderHeight={64}
          sx={{
            // Next several blocks ensure that borders show up correctly in dark mode
            borderColor: 'divider',
            '& .MuiDataGrid-toolbar': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-footerContainer': {
              bgcolor: 'background.default',
            },
            '& .MuiDataGrid-columnSeparator': {
              color: 'divider',
            },
            // zebra striping
            '& .MuiDataGrid-row:nth-of-type(odd):not(.Mui-selected)': {
              bgcolor: 'action.hover',
            },
            // allow header text to wrap
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: '1.2em',
            },
            // fix row height with wrapping text
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </Box>
    </Box>
  );
};

const MemoizedEvacuationZonesTable = memo(EvacuationZonesTable);

export { MemoizedEvacuationZonesTable as EvacuationZonesTable };
